import type { ModelViewerPresentationSettings } from '@/constants/modelViewerPresentation';
import type { Annotation } from '@/types/Annotation.model';
import type { ProductFormData } from '@/components/ProductForm/ProductForm';

export type ProductEditDirtySnapshot = {
  form: ProductFormData;
  linkSharePasswordSet: boolean;
  previewUri: string;
  cameraOrbit: string;
  cameraTarget: string;
  cameraFov: string;
  annotations: Annotation[];
  presentation: ModelViewerPresentationSettings;
  hasNewModelFile: boolean;
};

function stableStringify(value: unknown): string {
  return JSON.stringify(value);
}

/** Normalized fingerprint of everything that would be sent on save. */
export function fingerprintProductEditSnapshot(snapshot: ProductEditDirtySnapshot): string {
  const {
    form,
    linkSharePasswordSet,
    previewUri,
    cameraOrbit,
    cameraTarget,
    cameraFov,
    annotations,
    presentation,
    hasNewModelFile,
  } = snapshot;

  const linkShareDirty =
    form.linkShareMode === 'password' &&
    (form.updateLinkSharePassword || (!linkSharePasswordSet && form.linkSharePassword.trim().length > 0));

  return stableStringify({
    productName: form.productName,
    productPage: form.productPage,
    articleNumber: form.articleNumber,
    isPublic: form.isPublic,
    descriptionHtml: form.descriptionHtml ?? '',
    category: form.category ?? '',
    linkShareMode: form.linkShareMode,
    linkShareDirty,
    linkSharePassword: linkShareDirty ? form.linkSharePassword.trim() : '',
    metadata: form.metadata,
    previewUri,
    cameraOrbit,
    cameraTarget,
    cameraFov,
    annotations,
    presentation,
    hasNewModelFile,
  });
}

export function hasPendingProductEditChanges(
  baseline: ProductEditDirtySnapshot | null,
  current: ProductEditDirtySnapshot
): boolean {
  if (!baseline) return false;
  return fingerprintProductEditSnapshot(baseline) !== fingerprintProductEditSnapshot(current);
}
